<script lang="ts" setup>
import styled from 'vue-styled-components';
import { i18n } from '@/i18n';
import { CustomStyle } from '@/appLogic/interfaces/style';
import { appendImportantStyle } from '@/utils/display/styleUtil';
import { computed } from 'vue';
import { filterStringNumberUndefinedToString } from '@/coreDomainsModule/extends/string';
import VueDatePicker from '@vuepic/vue-datepicker';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';

/**
 * interface
 */
type InputType = 'recommendValue' | 'recommendCrossDate' | 'recommendNone';
// props
const props = withDefaults(
  defineProps<{
    inputType: InputType;
    textValue?: string | number;
    inputDate?: Date;
    placeholder?: string;
    labelDirection?: string;
    format?: string;
    customStyle?: CustomStyle;
    disabledDates?: ((date: Date) => boolean) | Date[];
  }>(),
  {
    labelDirection: 'row',
    textValue: '',
    customStyle: undefined,
    placeholder: '入力してください',
    inputDate: undefined,
    format: 'yyyy-MM-dd',
    /**
     * 指定した日付を選択不可とするかどうかを判定する関数
     */
    disabledDates: () => [],
  }
);

// emits
const emits = defineEmits<{
  (e: 'changeText', text: string): void;
  (e: 'changeValue', date: Date): void;
}>();

//変数
const t = i18n.t;
const locale = computed(() => i18n.locale);

//computed
const date = computed({
  /**
   * 値を取得する
   */
  get: () => props.inputDate,
  /**
   * 値を設定する
   * @param value -値
   */
  set: (value) => {
    // 値に変更があると呼ばれるsetter
    if (!value) {
      return;
    }
    emits('changeValue', value);
  },
});

/**
 *
 * @param value - 値
 */
const checkedValue = (value) => {
  return isNaN(Number(value)) ? undefined : Number(value);
};

const textComputed = computed({
  /**
   * 値を取得する
   */
  get: () => props.textValue,
  /**
   * 値を設定する
   * @param value -値
   */
  set: (value) => {
    // 呼び出し元に反映させる用
    emits('changeText', filterStringNumberUndefinedToString(value));
  },
});

// イベント

//関数
</script>

<template>
  <template v-if="inputType === 'recommendValue'">
    <div class="text-field-container">
      <NumberInputWithComma
        id="example1"
        :model-value="checkedValue(textComputed)"
        class="text-input text-field-container__text-input rounded-base-middle"
        :placeholder="t(placeholder)"
        :use-placeholder="true"
        @update:model-value="textComputed = $event"
      />
    </div>
  </template>
  <template v-else-if="inputType === 'recommendCrossDate'">
    <div class="RecommendOrderField">
      <VueDatePicker
        v-model="date"
        class="text-field-container__text-input"
        :mode-height="1"
        :format="format"
        :enable-time-picker="true"
        :month-change-on-scroll="false"
        :teleport="true"
        :disabled-dates="disabledDates"
        week-start="0"
        :locale="locale"
        :placeholder="t('日付を選択')"
        :select-text="t('選択')"
        :cancel-text="t('キャンセル')"
        :show-now-button="true"
        :clearable="false"
        :now-button-label="t('現在を選択')"
        pad
      />
    </div>
  </template>
  <template v-else-if="inputType === 'recommendNone'">
    <div class="text-field-container">
      <input
        id="example1"
        v-model="textComputed"
        class="text-input text-field-container__text-input rounded-base-middle"
        :placeholder="placeholder"
      />
    </div>
  </template>
</template>
<style lang="scss" scoped>
.text-field-container {
  margin: auto 0;
  max-width: 320px;
  font-size: $fontSizeS;
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  &__label {
    display: flex;
    justify-content: flex-start;
    font-weight: bold;
  }
  .text-input {
    display: block;
    text-align: center;
    font-size: $fontSizeS;
    margin: 0px 8px;
    padding: 4px 0px;
    &:focus {
      /* outline: 2px solid black; */
      opacity: 90%;
    }
    &:disabled {
      cursor: pointer;
    }
    // スピンボタンを非表示にする
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>

<style lang="scss">
/* - 以下VueDatePickerのCSS操作 - */

@font-face {
  font-family: 'Inter';
  src: url(~@/assets/font/Inter.ttf) format('truetype');
}

/* Inputフィールド全体のレイアウト */
.dp__input {
  width: 112px; // TODO: DatePickerがminWidthを持っているのでこんな感じで指定しているがなんとかしたい
  height: 28px;
  display: block;
  padding: 0;
  border-radius: 6px;
  font-family: Noto Sans JP;
  font-size: $fontSizeS;
  font-weight: 400;
  letter-spacing: 0em;
  text-align: center;
  color: $textSecondary;
}

/* NOTE: flexにより文字がボタンに収まらない問題を修正する */
.dp__action_buttons {
  flex: none;
  font-size: $fontSizeSS;
}

/* カレンダーアイコンは不要なので削除 */
.dp__input_icon {
  display: none;
}

/* Selectボタンが表示されない問題を修正する */
.dp__action_select {
  background-color: var(--dp-primary-color) !important;
}
</style>
